/**
 * Install Avida Banner — nudges signed-in users to add the app to
 * their home screen. Shown in three modes:
 *
 *   1. Chrome / Edge / Android: uses the browser's `beforeinstallprompt`
 *      capture and calls `promptInstall()` — one-tap install.
 *   2. iOS Safari: `beforeinstallprompt` never fires there, so we show a
 *      short "tap Share → Add to Home Screen" instruction card.
 *   3. Everywhere else (Firefox desktop etc.): stays hidden — no reliable
 *      install path.
 *
 * Never shown when:
 *   - The app already launched in `display-mode: standalone` (installed)
 *   - Running inside the Capacitor/Electron native shell
 *   - The user snoozed it in the last 7 days
 *   - The layout is in `?embed=1` mode
 */
import { useState, useEffect } from "react";
import { X, Download, Share, Plus } from "lucide-react";
import { onInstallAvailability, promptInstall } from "../../lib/pwa-register";

const SNOOZE_KEY = "avida:install-snooze-until";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  // PWA installed
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari flag
  if (window.navigator.standalone === true) return true;
  return false;
}

function isNativeShell() {
  if (typeof window === "undefined") return false;
  // Capacitor injects window.Capacitor; Electron injects window.electronAPI
  return !!(window.Capacitor || window.electronAPI);
}

function isIosSafari() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const webkit = /WebKit/i.test(ua);
  const notChrome = !/CriOS|FxiOS|EdgiOS|OPT|OPiOS/i.test(ua);
  return iOS && webkit && notChrome;
}

function isSnoozed() {
  try {
    const until = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    return until && Date.now() < until;
  } catch {
    return false;
  }
}

function snooze() {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
  } catch { /* private mode */ }
}

export default function InstallAppBanner() {
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState(null); // 'prompt' | 'ios'
  const [showIosSheet, setShowIosSheet] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone() || isNativeShell()) return;
    if (isSnoozed()) return;

    // iOS Safari — no programmatic prompt; show static instructions.
    if (isIosSafari()) {
      setMode("ios");
      setVisible(true);
      return;
    }

    // Chrome / Edge / Android — wait for the browser's install event.
    const off = onInstallAvailability((state) => {
      if (state.installed) {
        setVisible(false);
        return;
      }
      if (state.available) {
        setMode("prompt");
        setVisible(true);
      }
    });
    return off;
  }, []);

  const handleInstall = async () => {
    if (mode === "ios") {
      setShowIosSheet(true);
      return;
    }
    const res = await promptInstall();
    if (res.outcome === "accepted") {
      setVisible(false);
    } else if (res.outcome === "dismissed") {
      snooze();
      setVisible(false);
    }
  };

  const handleDismiss = () => {
    snooze();
    setVisible(false);
    setShowIosSheet(false);
  };

  if (!visible) return null;

  return (
    <>
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[80] w-[92vw] max-w-md rounded-2xl border border-slate-200 bg-white shadow-2xl"
        role="dialog"
        aria-label="Install Avida on this device"
        data-testid="install-banner"
      >
        <div className="flex items-start gap-3 p-4">
          <img
            src="/avida-icon.png"
            alt=""
            className="h-11 w-11 rounded-xl flex-shrink-0"
          />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-900">
              Install Avida on this device
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              {mode === "ios"
                ? "Add to your Home Screen for a full-screen app experience and push notifications."
                : "One tap to add it to your home screen — works offline and gets push notifications."}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={handleInstall}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A2B5C] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#062042] active:scale-[0.98] transition"
                data-testid="install-banner-install-btn"
              >
                <Download className="h-3.5 w-3.5" />
                {mode === "ios" ? "Show me how" : "Install"}
              </button>
              <button
                onClick={handleDismiss}
                className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1.5"
                data-testid="install-banner-later-btn"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="text-slate-400 hover:text-slate-600 -mr-1 -mt-1 p-1 rounded"
            data-testid="install-banner-close-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {showIosSheet && (
        <div
          className="fixed inset-0 z-[90] bg-black/50 flex items-end sm:items-center justify-center p-4"
          onClick={handleDismiss}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="install-banner-ios-sheet"
          >
            <div className="flex items-center gap-3">
              <img src="/avida-icon.png" alt="" className="h-12 w-12 rounded-xl" />
              <div>
                <div className="font-semibold text-slate-900">Add Avida to your Home Screen</div>
                <div className="text-xs text-slate-500">Takes about 5 seconds</div>
              </div>
            </div>
            <ol className="mt-4 space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-700">1</span>
                <div>
                  Tap the <Share className="inline h-4 w-4 -mt-0.5 text-blue-600" /> Share icon in
                  Safari's bottom toolbar.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-700">2</span>
                <div>
                  Scroll down and tap <span className="font-semibold">Add to Home Screen</span>
                  <Plus className="inline h-4 w-4 ml-1 -mt-0.5 text-slate-500" />
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-700">3</span>
                <div>Tap <span className="font-semibold">Add</span> — the Avida icon appears on your home screen.</div>
              </li>
            </ol>
            <button
              onClick={handleDismiss}
              className="mt-5 w-full rounded-lg bg-[#0A2B5C] py-2.5 text-sm font-semibold text-white hover:bg-[#062042] transition"
              data-testid="install-banner-ios-close-btn"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
